import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY_ITEMS, GALLERY_FILTERS } from "../data";
import { Reveal } from "./Reveal";

export const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback(
    (dir) => setLightbox((cur) => (cur === null ? null : (cur + dir + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (lightbox === null) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, close, step]);

  const active = lightbox !== null ? items[lightbox] : null;

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-ivory py-16 sm:py-20 lg:py-28">
      <div className="section-wrap">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Gallery</p>
          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Glimpses of Kasdol Hotel
          </h2>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap justify-center gap-2.5">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              data-testid={f.testid}
              onClick={() => {
                setFilter(f.value);
                setLightbox(null);
              }}
              aria-pressed={filter === f.value}
              className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                filter === f.value
                  ? "border-navy bg-navy text-cream"
                  : "border-navy/20 bg-white text-navy/70 hover:border-gold hover:text-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </Reveal>

        <Reveal className="mt-10">
          <div data-testid="gallery-grid" className="columns-2 gap-4 lg:columns-3 [&>button]:mb-4">
            {items.map((item, i) => (
              <button
                key={item.title}
                type="button"
                data-testid={`gallery-item-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setLightbox(i)}
                aria-label={`Open image: ${item.title}`}
                className="group block w-full break-inside-avoid overflow-hidden rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                <img
                  src={item.url}
                  alt={`${item.title} — ${item.category} at Kasdol Hotel`}
                  loading="lazy"
                  className={`${item.height} w-full object-cover transition-transform duration-700 group-hover:scale-105`}
                />
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`Image viewer: ${active.title}`}
          className="fixed inset-0 z-[80] flex flex-col bg-navy/95 backdrop-blur-md"
          onClick={close}
        >
          <button
            type="button"
            data-testid="gallery-lightbox-close"
            aria-label="Close image viewer"
            onClick={close}
            className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-sm border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="flex flex-1 items-center justify-center px-4 py-16 sm:px-16" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              data-testid="gallery-lightbox-prev"
              aria-label="Previous image"
              onClick={() => step(-1)}
              className="absolute left-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold sm:left-6"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <figure className="max-w-5xl text-center">
              <img
                src={active.url}
                alt={`${active.title} — ${active.category} at Kasdol Hotel`}
                className="max-h-[70vh] w-auto max-w-full rounded-md border border-gold/25 object-contain"
              />
              <figcaption className="mt-4">
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">{active.category}</span>
                <p className="mt-1 font-serif text-xl text-cream">{active.title}</p>
              </figcaption>
            </figure>

            <button
              type="button"
              data-testid="gallery-lightbox-next"
              aria-label="Next image"
              onClick={() => step(1)}
              className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold sm:right-6"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
